/**
 * Admin / non-analytics views menu. Renders in the sidebar's nav region
 * when the hamburger in `SidebarFooter` is open. Clicking an item sets
 * `?view=<id>` and the main canvas swaps to the matching admin page.
 *
 * The header uses the same `TabStrip` instance as the main canvas so
 * the seam, height, and bottom border match the rest of the shell. The
 * item list mirrors `ScopeSidebar` typography (`ROOT_NAV_TITLE_CLASS`
 * + `ROOT_NAV_META_CLASS`) so when settings later grow sub-sections
 * we can drop `StickyFolder` in without restyling the menu chrome.
 */
import TabStrip from "./TabStrip.jsx";
import { ENTITLEMENTS, hasEntitlement } from "../lib/entitlements.js";
import { NOTE_CLASS, ROOT_NAV_TITLE_CLASS } from "../lib/ui.js";
import { agentPolicyFeatureEnabled, securityTabEnabled, skillsFeatureEnabled } from "../lib/featureFlags.js";
import { phAttrs } from "../lib/analytics/attrs.js";
import { ATTRIBUTE } from "../lib/analytics/surfaceVocabulary.js";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { ADMIN_VIEW_MODES, MODE_IDS, shownViewModes } from "../lib/routeModel.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";

// Top-level modes of the app, selected from the rail buttons.
// `explore` is the scope-explorer (the main analytics
// surface) and owns no admin views. `studio` is the authoring surface
// for the Library — kernels, canvases, and workflows.
// `endpoints` is day-to-day Origin Agent fleet work. `governance` is
// security and IT policy for AI services. `admin` is organization-wide
// access, identity, and ingestion administration.
//
// The rail's bracket numerals are positional fallbacks (only shown if a mode
// has no icon): [0] dashboard (the default landing), [1] explore, [2] studio,
// [3] endpoints, [4] governance and [5] admin, with the Venom wordmark pinned
// at the bottom.
const MODE_LABELS = {
    workspace: { rail: "W", label: "Workspace", note: "Find sessions, lay them on one clock, and read them with the chat" },
    dashboard: { rail: "0", label: "Dashboard", note: "Your dashboard & chat history" },
    explore: { rail: "1", label: "Explore", note: "Scope explorer" },
    security: { rail: "2", label: "Security", note: "Explore findings and investigate selected activity" },
    studio: { rail: "3", label: "Library", note: "Kernels, canvases, workflows, and skills" },
    endpoints: { rail: "4", label: "Endpoints", note: "Connect, monitor, and configure endpoints" },
    governance: { rail: "5", label: "AI Governance", note: "Sanctioned AI and detection policy" },
    admin: { rail: "6", label: "Admin", note: "Organization administration" },
};

// The ids and their order come from routeModel.js, which the analytics layer
// reads too.
export const MODES = MODE_IDS.map((id) => ({ id, ...MODE_LABELS[id] }));

// The Skills view is gated by a legacy localStorage feature flag (preserved
// from the prior dashboard release):
//   localStorage.setItem("origin.feature.skills", "1"); location.reload();
// Filter happens at module load — toggling the flag requires a reload,
// matching the legacy contract. App.jsx's ADMIN_VIEW_IDS derives from
// this list, so the URL router naturally rejects a `?view=skills` when
// the flag is off (no double-gating needed downstream).
//
// `permission` is the RBAC nav gate (replaces the old `adminOnly`
// boolean). A view is shown only when the caller holds that permission
// in their flattened set — never gated on a role NAME (custom roles
// land in v2; name-gating would break). The gate is the page's READ
// permission ("show me what I can see"); in-page manage/execute actions
// gate separately (see ProvisioningTokensPage, SettingsPage). Views with
// no `permission` (studio authoring, feedback) are visible to everyone.
// Each view's mode comes from routeModel.js.
const ALL_ADMIN_VIEWS = [
    // Library (studio) surfaces. Kernels/Canvases/Workflows are saved analytics
    // artifacts — useless without analytics, so gate them on the analytics-execute
    // verb (a role that can't query the graph can't run a recipe/canvas/workflow).
    // A role without analytics sees no studio views, so the Library rail entry
    // hides entirely (see App.jsx rail gate).
    // Discovery is the Security landing view. Explicit Alerts URLs retain the
    // investigation queue, with the same subject-read permission boundary.
    // All three share the Security tab's flag (see routeModel.js): with it off
    // they leave this list together, which hides the Security rail entry
    // (firstVisibleViewForMode returns null) and rejects every ?adminView=
    // into Security.
    { id: "security-overview", label: "Overview", note: "Findings across your organization and saved investigations", permission: PERMISSIONS.AI_SESSIONS_READ },
    { id: "discovery",   label: "Discovery",               note: "Explore review findings across visible activity", permission: PERMISSIONS.AI_SESSIONS_READ },
    { id: "alerts",      label: "Investigations",          note: "Review saved sets of findings and their evidence", permission: PERMISSIONS.AI_SESSIONS_READ },
    { id: "kernels",     label: "Kernels",                 note: "Saved deterministic recipes", permission: PERMISSIONS.ANALYTICS_EXECUTE },
    { id: "canvases",    label: "Canvases",                note: "Saved multi-widget views", permission: PERMISSIONS.ANALYTICS_EXECUTE },
    { id: "workflows",   label: "Workflows",               note: "Saved agentic analysis runs", permission: PERMISSIONS.ANALYTICS_EXECUTE },
    { id: "skills",      label: "Skills",                  note: "Upload and deploy skill archives" },

    // Endpoint operations. Inventory is the established-fleet default;
    // Connect adapts across token/installer/check-in permissions for onboarding.
    { id: "endpoints",       label: "Overview",              note: "Inventory and endpoint state", permission: PERMISSIONS.ENDPOINT_METADATA_READ },
    { id: "unassigned-endpoints", label: "Unassigned", note: "Endpoints without a directory-user assignment", permission: { confirmed: PERMISSIONS.USERS_MANAGE }, entitlement: ENTITLEMENTS.ABAC },
    // The composed enrollment mission (token + installer + first check-in).
    // OR-gated on the three read surfaces it fronts — a role holding any one
    // of them gets a usable page (each section adapts to what it can do).
    { id: "connect",         label: "Connect",               note: "Install the agent and verify check-in", permission: { any: [PERMISSIONS.PROVISIONING_TOKENS_READ, PERMISSIONS.INSTALLERS_READ, PERMISSIONS.ENDPOINT_METADATA_READ] } },
    { id: "endpoint-config", label: "Configuration",         note: "Settings applied to all endpoints", permission: { any: [PERMISSIONS.TENANT_CONFIG_WRITE, PERMISSIONS.TELEMETRY_EXPORT_READ] } },
    { id: "rollouts",        label: "Artifact Rollouts",      note: "Agent and Content Update release controls", permission: PERMISSIONS.TENANT_CONFIG_READ, entitlement: ENTITLEMENTS.ARTIFACT_ROLLOUT_CONTROL },
    { id: "tokens",          label: "Provisioning Tokens",    note: "Advanced enrollment credentials", permission: PERMISSIONS.PROVISIONING_TOKENS_READ, menu: false },
    { id: "installers",      label: "Installers",             note: "Advanced agent downloads", permission: PERMISSIONS.INSTALLERS_READ, menu: false },

    // AI governance is its own mode so these controls stay out of the
    // general Admin bucket. Agent Policy leads, so it is the mode's landing
    // view whenever its flag is on and the caller can read it.
    { id: "agent-policy", label: "Agent Policy", note: "Constrain agent behavior by deploying policy fleet-wide", permission: PERMISSIONS.AGENT_CONFIG_READ },
    { id: "ai-services", label: "Unsanctioned AI Detection", note: "Define which agents are sanctioned by your organization", permission: PERMISSIONS.TENANT_CONFIG_READ },

    // Personal settings are available to every signed-in user, including
    // members without organization-management permissions.
    { id: "account",     label: "User Settings",              note: "Your access, sign-in, and local data" },

    // Organization administration.
    { id: "users",       label: "Access",                    note: "Application users and roles", permission: { any: [PERMISSIONS.USERS_READ, PERMISSIONS.GRANTS_MANAGE] } },
    // Directory page hosts two surfaces under one nav entry: the
    // Integrations connector table (needs integrations.read) and the
    // Users/Groups/Devices directory browser (needs directory_data.read).
    // Gate the entry on EITHER — the page itself filters its tab list per
    // permission and lands on the first visible tab, so a role with just
    // one of the two still gets a usable page instead of a 403'd default.
    { id: "directory",    label: "Identity",                  note: "Connections and synced directory data", permission: { any: [PERMISSIONS.INTEGRATIONS_READ, PERMISSIONS.DIRECTORY_DATA_READ] } },
    { id: "escalations", label: "Escalations",               note: "Time-boxed access requests and approvals", permission: { any: [PERMISSIONS.ESCALATIONS_REQUEST, PERMISSIONS.ESCALATIONS_APPROVE] } },
    // Connector credentials still use the transitional admin shim
    // (`tenant.users.manage`). OR it with the page's existing tenant-config
    // gate so the credential-health action always reaches its target.
    { id: "data-sources", label: "Data Sources",              note: "External AI activity connectors", permission: { any: [PERMISSIONS.USERS_MANAGE, PERMISSIONS.TENANT_CONFIG_WRITE] } },
    // Its own entry rather than a Data Sources card: that page is inbound
    // capture, this is outbound delivery. Gated on integrations rather than
    // tenant config because that is what the CRUD RPCs behind the page check;
    // riding tenant.config.write would show the page to callers the server
    // rejects and hide it from ones it allows.
    { id: "notifications", label: "Notifications",            note: "Send detections to Slack or Microsoft Teams", permission: PERMISSIONS.INTEGRATIONS_WRITE },
    { id: "report",       label: "Send Feedback",              note: "Report a bug or request a feature", menu: false },
].map((view) => ({ ...view, mode: ADMIN_VIEW_MODES[view.id] }));

// The views this build shows, with their modes. The analytics layer reads
// the same flags through routeModel.js.
export const SHOWN_VIEW_MODES = shownViewModes({ securityEnabled: securityTabEnabled(), skillsEnabled: skillsFeatureEnabled(), agentPolicyEnabled: agentPolicyFeatureEnabled() });

export const ADMIN_VIEWS = ALL_ADMIN_VIEWS.filter((v) => v.id in SHOWN_VIEW_MODES);

// The RBAC permission gate a view's nav entry carries, or null if it's
// visible to everyone. May be a single permission string OR an `{ any:
// [...] }` OR-gate (the Directory page, which fronts two independently
// permissioned surfaces). Single source of truth shared by the nav
// (AdminMenu) and the direct-URL guard (AdminPage).
export function permissionForView(viewId) {
    return ADMIN_VIEWS.find((v) => v.id === viewId)?.permission ?? null;
}

// Resolve a view's permission gate against the caller's posture. Accepts:
//   - null/undefined → no gate, always allowed.
//   - a string       → single-permission gate (hasPermission).
//   - { any: [...] }  → OR-gate; allowed if the caller holds ANY listed
//                       permission (an empty list denies — a misdeclared
//                       gate must fail closed, not show everything).
//   - { confirmed: permission } requires known standing permission, independent
//                       of activity scope health. Unknown posture denies.
// hasPermission fails open while caps load/are unavailable, so any
// concrete entry short-circuits the gate open during the fail-open
// window — a pre-RBAC tenant keeps the full nav.
export function allowsView(permission, caps) {
    if (permission == null) return true;
    if (typeof permission === "string") return hasPermission(caps, permission);
    if (permission.confirmed) return Boolean(caps?.available && !caps.loading && !caps.degraded && caps.permissions.has(permission.confirmed));
    const list = Array.isArray(permission.any) ? permission.any : [];
    return list.some((p) => hasPermission(caps, p));
}

// Admin views for `mode`, filtered to those the caller's permission set
// allows. Pure (caps in, list out) so the per-role nav matrix is unit
// testable without rendering. `allowsView` fails open while caps are
// loading/unavailable, so a pre-RBAC tenant sees the full settings nav
// exactly as before. Entitlement-gated views still require a confirmed grant.
export function visibleAdminViews(views, mode, caps, entitlements = null) {
    return views.filter(
        (v) => v.mode === mode && v.menu !== false && allowsView(v.permission, caps)
            && (!v.entitlement || hasEntitlement(entitlements, v.entitlement)),
    );
}

// Mode of a given admin view id, or null if the id isn't a registered
// admin view (e.g. when no view is selected, the mode is explore — caller
// handles that fallback).
export function modeForView(viewId) {
    const def = ADMIN_VIEWS.find((v) => v.id === viewId);
    return def?.mode || null;
}

// First view registered for a given mode. Used when the rail mode
// button is clicked: we land on this view as the default destination.
// Returns null for `explore` (which has no admin views).
export function firstViewForMode(modeId) {
    const def = ADMIN_VIEWS.find((v) => v.mode === modeId && v.menu !== false);
    return def?.id || null;
}

export function firstVisibleViewForMode(modeId, caps, entitlements = null) {
    return visibleAdminViews(ADMIN_VIEWS, modeId, caps, entitlements)[0]?.id || null;
}

function MenuRow({ view, isActive, onSelect }) {
    return (
        <button
            type="button"
            role="menuitem"
            aria-current={isActive ? "page" : undefined}
            onClick={() => onSelect(view.id)}
            {...phAttrs({ [ATTRIBUTE.NAV]: view.id })}
            className={[
                "relative grid w-full appearance-none items-center gap-x-2 border-0 bg-transparent py-[8px] pl-[6px] pr-[9px] text-left text-[color:var(--muted)] transition-colors cursor-pointer",
                "[grid-template-columns:14px_minmax(0,1fr)_auto]",
                isActive
                    ? "isolate before:absolute before:inset-0 before:-z-10 before:bg-[color:var(--green-soft)] before:shadow-[inset_2px_0_0_var(--green)] text-[color:var(--ink)]"
                    : "hover:text-[color:var(--ink)]",
            ].join(" ")}
        >
            <span className="grid h-[14px] w-[14px] place-items-center text-[color:var(--muted-soft)]" aria-hidden="true">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="h-[10px] w-[10px]">
                    <path d="M4 8h8" />
                </svg>
            </span>
            <span className="grid min-w-0 gap-[2px]">
                <span className={ROOT_NAV_TITLE_CLASS}>{view.label}</span>
                <span className={`${NOTE_CLASS} truncate text-[10px] leading-[1.2] text-[color:var(--muted-soft)] normal-case tracking-normal`}>
                    {view.note}
                </span>
            </span>
            <span aria-hidden="true" />
        </button>
    );
}

export default function AdminMenu({ activeView, entitlements = null, mode = "admin", onSelect, onCollapseDrawer }) {
    // Filter the global ADMIN_VIEWS list to the active mode so the menu
    // only ever shows views relevant to the rail bracket the user
    // pressed. Default to Admin when a caller
    // doesn't supply a mode.
    const modeDef = MODES.find((m) => m.id === mode);
    // Permission-gated views are hidden when the caller's set lacks the
    // view's permission. While the set is still loading (or unavailable
    // on a pre-RBAC tenant) `hasPermission` fails open, so the nav looks
    // exactly as it did before RBAC rather than flash-hiding every row.
    const caps = useMyPermissions();
    const visibleViews = visibleAdminViews(ADMIN_VIEWS, mode, caps, entitlements);

    // Single non-interactive tab using the canvas TabStrip. Labelled
    // with the current mode so the user always knows
    // which bracket they're inside.
    const headerTabs = [
        { id: "admin", label: modeDef?.label || "Menu", active: true },
    ];

    // Drawer collapse — mirrors StudioMenu's affordance so every
    // non-explore sidebar gets a consistent way to collapse the
    // whole drawer.
    const headerActions = onCollapseDrawer ? (
        <div className="relative flex min-h-[38px] shrink-0 items-stretch overflow-hidden border border-b-0 border-[color:var(--line)] bg-[color:var(--control-bg)]">
            <button
                type="button"
                onClick={onCollapseDrawer}
                title="Collapse drawer"
                aria-label="Collapse drawer"
                className="inline-flex w-[28px] shrink-0 items-center justify-center px-[8px] text-[color:var(--muted-soft)] hover:text-[color:var(--muted)] transition-colors [&_svg]:block [&_svg]:h-[12px] [&_svg]:w-[12px]"
            >
                <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 3.5 5.5 8 10 12.5" />
                </svg>
            </button>
        </div>
    ) : null;

    return (
        <nav
            data-admin-menu
            className="flex h-full min-h-0 min-w-0 flex-col bg-[color:var(--bg)]"
            aria-label="Admin navigation"
        >
            <TabStrip
                tabs={headerTabs}
                actions={headerActions}
                className="pt-[11px] max-[900px]:pt-[8px]"
            />
            <ul className="flex flex-1 min-h-0 flex-col overflow-y-auto pt-[10px] pb-[12px]">
                {visibleViews.map((v) => (
                    <li key={v.id}>
                        <MenuRow
                            view={v}
                            isActive={v.id === activeView}
                            onSelect={onSelect}
                        />
                    </li>
                ))}
            </ul>
        </nav>
    );
}
